import React from "react";
import { ShieldCheck, ShieldAlert, MapPin, Clock, ExternalLink } from "lucide-react";

/**
 * Trust Layer badge.
 * Attaches jurisdiction · sources · last-checked · confidence to any legal
 * answer. When confidence === "unverified" the caveat is shown prominently.
 */
export default function TrustBadge({ trust, compact = false }) {
  if (!trust) return null;
  const conf = (trust.confidence || "low").toLowerCase();
  const unverified = conf === "unverified";
  const tone = unverified ? "danger" : conf === "high" ? "success" : conf === "medium" ? "info" : "warn";
  const Icon = unverified ? ShieldAlert : ShieldCheck;

  const colorMap = {
    success: "border-emerald-600 bg-emerald-50 text-emerald-900",
    info: "border-[#7B56C9] bg-[#7B56C9]/8 text-[#2A0F5A]",
    warn: "border-[#C5A059] bg-[#C5A059]/10 text-[#8a6b1f]",
    danger: "border-red-700 bg-red-50 text-red-900",
  };
  const box = colorMap[tone];

  return (
    <div className={`border-l-2 ${box} px-3 py-2 text-xs`} data-testid="trust-badge">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} />
        <span className="label-eyebrow !text-[10px]">Trust · {conf}</span>
        {trust.jurisdiction && (
          <span className="flex items-center gap-1 opacity-80"><MapPin size={10} /> {trust.jurisdiction}</span>
        )}
        {trust.last_checked && (
          <span className="flex items-center gap-1 opacity-80"><Clock size={10} /> {trust.last_checked}</span>
        )}
      </div>
      {unverified && trust.caveat && (
        <div className="italic mb-1" data-testid="trust-caveat">{trust.caveat}</div>
      )}
      {!compact && (trust.sources?.length ?? 0) > 0 && (
        <ul className="list-disc pl-5 space-y-0.5 opacity-90">
          {trust.sources.map((s, i) => (
            <li key={i} className="flex items-start gap-1">
              <span>{s}</span>
            </li>
          ))}
        </ul>
      )}
      {!compact && (trust.sources?.length ?? 0) === 0 && !unverified && (
        <div className="italic opacity-70">No specific sources cited. Verify against your jurisdiction before acting.</div>
      )}
    </div>
  );
}
